import { Component } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Inject } from '@angular/core';

export interface ManagePdfReportDialogReportOption {
    reportGuid: string;
    title: string;
    description?: string;
    fileName?: string;
}

export interface ManagePdfReportRequestDialogData {
    existingReports: ManagePdfReportDialogReportOption[];
}

export interface ManagePdfReportRequestPayload {
    mode: 'create' | 'update';
    reportGuid?: string;
    title: string;
    fileName: string;
    description: string;
    runNow: boolean;
}

@Component({
    selector: 'app-manage-pdf-report-request-dialog',
    templateUrl: './manage-pdf-report-request-dialog.component.html',
    styleUrls: ['./manage-pdf-report-request-dialog.component.scss']
})
export class ManagePdfReportRequestDialogComponent {
    readonly form = this.formBuilder.group({
        mode: ['create' as 'create' | 'update'],
        selectedReportGuid: [null as string | null],
        title: ['', [Validators.required, Validators.maxLength(200)]],
        fileName: ['', [Validators.maxLength(255)]],
        description: ['', [Validators.maxLength(1000)]],
        runNow: [true]
    });

    readonly existingReports: ManagePdfReportDialogReportOption[];

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly dialogRef: MatDialogRef<ManagePdfReportRequestDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data: ManagePdfReportRequestDialogData
    ) {
        this.existingReports = data?.existingReports || [];
    }

    cancel(): void {
        this.dialogRef.close();
    }

    submit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const value = this.form.getRawValue();
        if (value.mode === 'update' && !value.selectedReportGuid) {
            this.form.controls.selectedReportGuid.setErrors({ required: true });
            this.form.markAllAsTouched();
            return;
        }

        const title = value.title || '';
        const fileName = this.normalizeFileName(value.fileName || title || 'pdf-report');

        this.dialogRef.close({
            mode: value.mode || 'create',
            reportGuid: value.selectedReportGuid || undefined,
            title,
            fileName,
            description: value.description || '',
            runNow: !!value.runNow
        } as ManagePdfReportRequestPayload);
    }

    onModeChanged(mode: 'create' | 'update'): void {
        this.form.patchValue({ mode });
        if (mode === 'create') {
            this.form.patchValue({ selectedReportGuid: null });
            this.form.controls.selectedReportGuid.setErrors(null);
        }
    }

    onSelectedReportChanged(reportGuid: string | null): void {
        const selectedReport = this.existingReports.find((item) => item.reportGuid === reportGuid);
        if (!selectedReport) {
            return;
        }

        this.form.patchValue({
            title: selectedReport.title || '',
            description: selectedReport.description || '',
            fileName: selectedReport.fileName || ''
        });
    }

    private normalizeFileName(value: string): string {
        const cleaned = (value || '')
            .trim()
            .replace(/[\\/:*?"<>|]+/g, '_')
            .replace(/\s+/g, '-');
        return cleaned.toLowerCase().endsWith('.pdf') ? cleaned : `${cleaned}.pdf`;
    }
}
